import { useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Permission, useAddUsersMutation } from "src/store/api/generatedApi";
import { fetchErrorDetail } from "helpers";
import {
  CLARK_USER_PERMISSION,
  PERMISSION_VALUES,
} from "components/users/permissions.ts";
import {
  ClarkProfileFields,
  ClarkProfileForm,
  EMPTY_CLARK_PROFILE_FIELDS,
  isClarkProfileComplete,
  useSaveClarkProfile,
} from "components/users/clarkProfileSection";
import { useTenantTranslation } from "hooks/formatters";
import { useNotifyStatus } from "src/contexts/status";
import { MultiSelect } from "src/components/common/inputs/multiSelect";

export const AddUsersDialog = ({
  tenant,
  isOpen,
  onClose,
}: {
  tenant: string;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTenantTranslation();
  const notify = useNotifyStatus();
  const [emailsText, setEmailsText] = useState("");
  const [permissions, setPermissions] = useState<Permission[]>(["read"]);
  const [sendEmail, setSendEmail] = useState(false);
  const [clarkProfileFieldsByEmail, setClarkProfileFieldsByEmail] = useState<
    Record<string, ClarkProfileFields>
  >({});
  const [savedProfileEmails, setSavedProfileEmails] = useState<string[]>([]);
  const [areUsersAdded, setAreUsersAdded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addUsers] = useAddUsersMutation();
  const clarkProfile = useSaveClarkProfile({ tenant });

  const parsedEmails = emailsText
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter((email) => email.length > 0);
  const hasEmails = parsedEmails.length > 0;
  const grantsClarkUser = permissions.includes(CLARK_USER_PERMISSION);
  const clarkProfileEmails = grantsClarkUser
    ? [...new Set(parsedEmails)].filter(
        (email) => !savedProfileEmails.includes(email)
      )
    : [];
  const clarkProfileFieldsOf = (email: string) =>
    clarkProfileFieldsByEmail[email] ?? EMPTY_CLARK_PROFILE_FIELDS;

  const resetAndClose = () => {
    setEmailsText("");
    setPermissions(["read"]);
    setSendEmail(false);
    setClarkProfileFieldsByEmail({});
    setSavedProfileEmails([]);
    clarkProfile.clearSaveErrors();
    setAreUsersAdded(false);
    onClose();
  };

  const addUsersOnce = async () => {
    if (areUsersAdded) {
      return true;
    }
    try {
      await addUsers({
        tenantName: tenant,
        addUsersRequest: {
          emails: parsedEmails,
          permissions,
          send_email: sendEmail,
        },
      }).unwrap();
      setAreUsersAdded(true);
      return true;
    } catch (error) {
      notify({
        type: "error",
        text: fetchErrorDetail(error) ?? t("couldntAddUsers"),
      });
      return false;
    }
  };

  const saveClarkProfiles = async () => {
    const outcomes = await Promise.allSettled(
      clarkProfileEmails.map((email) =>
        clarkProfile.saveKeepingExistingHandles({
          email,
          fields: clarkProfileFieldsOf(email),
        })
      )
    );
    const newlySavedEmails = clarkProfileEmails.filter(
      (_email, index) => outcomes[index]?.status === "fulfilled"
    );
    setSavedProfileEmails((current) => [...current, ...newlySavedEmails]);
    return newlySavedEmails.length === clarkProfileEmails.length;
  };

  const handleAddUsers = async () => {
    setIsSubmitting(true);
    try {
      if ((await addUsersOnce()) && (await saveClarkProfiles())) {
        notify({ type: "success", text: t("usersAdded") });
        resetAndClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        if (!isSubmitting) {
          resetAndClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("addUsers")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <InputLabel>{t("emails")}</InputLabel>
          <TextField
            multiline
            minRows={4}
            fullWidth
            value={emailsText}
            disabled={areUsersAdded || isSubmitting}
            onChange={(e) => {
              setEmailsText(e.target.value);
            }}
            placeholder={t("addUsersEmailsHint")}
          />
          <Typography variant="caption" color="text.secondary">
            {t("nEmailsParsed", { count: parsedEmails.length })}
          </Typography>

          <InputLabel>{t("initialPermissions")}</InputLabel>
          <MultiSelect<Permission, Permission>
            options={Array.from(PERMISSION_VALUES)}
            values={permissions}
            setValues={setPermissions}
            disabled={areUsersAdded || isSubmitting}
            getOptionId={(permission) => permission}
            getOptionLabel={(permission) => permission}
            label={t("permissions")}
            sx={{ minWidth: 260 }}
          />
          {grantsClarkUser ? (
            <Typography variant="caption" color="text.secondary">
              {t("clarkOnlyInviteHint")}
            </Typography>
          ) : null}

          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                onChange={(e) => {
                  setSendEmail(e.target.checked);
                }}
              />
            }
            label={t("sendInviteEmailToUsers")}
            disabled={areUsersAdded || isSubmitting}
          />

          {clarkProfileEmails.length === 0 ? null : (
            <Stack gap={1.5}>
              <Typography variant="subtitle1">{t("clarkProfile")}</Typography>
              {clarkProfileEmails.map((email, index) => (
                <Stack key={email} gap={1}>
                  {index === 0 ? null : <Divider />}
                  <ClarkProfileForm
                    fields={clarkProfileFieldsOf(email)}
                    updateFields={(update) => {
                      setClarkProfileFieldsByEmail((fieldsByEmail) => ({
                        ...fieldsByEmail,
                        [email]: { ...clarkProfileFieldsOf(email), ...update },
                      }));
                    }}
                    email={email}
                    showsMissingFields
                    saveError={clarkProfile.saveErrors[email] ?? null}
                    disabled={isSubmitting}
                  />
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose} disabled={isSubmitting}>
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          loading={isSubmitting}
          disabled={
            !hasEmails ||
            clarkProfileEmails.some(
              (email) => !isClarkProfileComplete(clarkProfileFieldsOf(email))
            )
          }
          onClick={() => void handleAddUsers()}
        >
          {t("addUsers")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
